// @ts-check
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Ids between two ids (inclusive) in list order - what shift-click selects.
 * Falls back to just `to` when either end isn't in the list.
 *
 * @param {string[]} ids
 * @param {string | null} from
 * @param {string} to
 */
export function idsInRange(ids, from, to) {
  const a = from == null ? -1 : ids.indexOf(from);
  const b = ids.indexOf(to);
  if (a === -1 || b === -1) return [to];
  return ids.slice(Math.min(a, b), Math.max(a, b) + 1);
}

/**
 * Row selection for a list/table page.
 *
 * @param {string[]} ids - ids of the rows currently shown, in display order
 * @param {{ resetKey?: unknown }} [opts] - selection clears whenever this changes
 *   (pass the active filter / search / page so a stale selection can't be acted on)
 */
export default function useRowSelection(ids, { resetKey } = {}) {
  const [picked, setPicked] = useState(/** @type {Set<string>} */ (new Set()));
  const lastToggled = useRef(/** @type {string | null} */ (null));

  useEffect(() => {
    setPicked(new Set());
    lastToggled.current = null;
  }, [resetKey]);

  // Only ids still on screen count, so a reload that drops a row can't leave a ghost selection.
  const selectedIds = useMemo(() => ids.filter((id) => picked.has(id)), [ids, picked]);
  const count = selectedIds.length;
  const allSelected = ids.length > 0 && count === ids.length;
  const someSelected = count > 0 && !allSelected;

  const isSelected = useCallback((/** @type {string} */ id) => picked.has(id), [picked]);

  const toggle = useCallback(
    (/** @type {string} */ id, { shift = false } = {}) => {
      const anchor = lastToggled.current; // read now: the updater below runs later, after the ref moves on
      setPicked((prev) => {
        const next = new Set(prev);
        const targets = shift ? idsInRange(ids, anchor, id) : [id];
        // A range takes the state the clicked row is moving to
        const select = !prev.has(id);
        for (const t of targets) {
          if (select) next.add(t);
          else next.delete(t);
        }
        return next;
      });
      lastToggled.current = id;
    },
    [ids],
  );

  const toggleAll = useCallback(() => {
    setPicked(allSelected ? new Set() : new Set(ids));
    lastToggled.current = null;
  }, [allSelected, ids]);

  const clear = useCallback(() => {
    setPicked(new Set());
    lastToggled.current = null;
  }, []);

  return { selectedIds, count, allSelected, someSelected, isSelected, toggle, toggleAll, clear };
}
